
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ProductGroup } from "@/types/product";
import { sendPricingRequest, EmailTemplateParams } from "@/utils/emailService";
import PersonalInfoSection from "./form-sections/PersonalInfoSection";
import CompanyInfoSection from "./form-sections/CompanyInfoSection";
import ContactInfoSection from "./form-sections/ContactInfoSection";
import AddressSection from "./form-sections/AddressSection";
import ProductInterestSection from "./form-sections/ProductInterestSection";
import SelectedProductsSection from "./form-sections/SelectedProductsSection";

export interface FormData {
  firstName: string;
  lastName: string;
  companyName: string;
  jobTitle: string;
  email: string;
  mobile: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  postalCode: string;
  interests: string[];
}

interface PricingRequestFormProps {
  productGroups: ProductGroup[];
}

const PricingRequestForm = ({ productGroups }: PricingRequestFormProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    companyName: "",
    jobTitle: "",
    email: "",
    mobile: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    postalCode: "",
    interests: []
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (id: string, checked: boolean) => {
    setFormData(prev => {
      if (checked) {
        return { ...prev, interests: [...prev.interests, id] };
      } else {
        return { ...prev, interests: prev.interests.filter(item => item !== id) };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Map form data to EmailJS template parameters according to the template
      const templateParams: EmailTemplateParams = {
        first_name: formData.firstName,
        last_Name: formData.lastName, // Note: Using the exact capitalization from the template
        company_name: formData.companyName,
        job_title: formData.jobTitle,
        email_address: formData.email,
        mobile_number: formData.mobile,
        Company_Address_Line_1: formData.addressLine1, // Matching the template capitalization
        Company_Address_Line_2: formData.addressLine2,
        Company_Address_Line_3: formData.addressLine3,
        Postal_Code: formData.postalCode,
        interests: formData.interests.map(id => {
          const group = productGroups.find(g => g.id === id);
          return group ? group.name : id;
        }).join(", "),
        message: `Pricing request from ${formData.firstName} ${formData.lastName} at ${formData.companyName}`,
        from_name: `${formData.firstName} ${formData.lastName}`,
        to_name: "Sales Team"
      };
      
      await sendPricingRequest(templateParams);
      
      toast.success("Your pricing request has been submitted successfully.");
      
      setFormData({
        firstName: "",
        lastName: "",
        companyName: "",
        jobTitle: "",
        email: "",
        mobile: "",
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        postalCode: "",
        interests: []
      });
      
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to submit your request. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PersonalInfoSection 
        firstName={formData.firstName}
        lastName={formData.lastName}
        onChange={handleChange}
      />
      
      <CompanyInfoSection 
        companyName={formData.companyName}
        jobTitle={formData.jobTitle}
        onChange={handleChange}
      />
      
      <ContactInfoSection
        email={formData.email}
        mobile={formData.mobile}
        onChange={handleChange}
      />
      
      <AddressSection
        addressLine1={formData.addressLine1}
        addressLine2={formData.addressLine2}
        addressLine3={formData.addressLine3}
        postalCode={formData.postalCode}
        onChange={handleChange}
      />
      
      <ProductInterestSection
        productGroups={productGroups}
        selectedInterests={formData.interests}
        onInterestChange={handleInterestChange}
      />
      
      <SelectedProductsSection 
        selectedInterests={formData.interests}
        productGroups={productGroups}
      />
      
      <div className="flex justify-center pt-4">
        <Button 
          type="submit" 
          size="lg" 
          className="px-8" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default PricingRequestForm;
